import { HeartBank } from "../../../managers/HeartBank.ts";
import { Crypto } from "../../../managers/Crypto.ts";
import template from "./hb-safe.mjs";
import { render } from "lit-html";
import { fs } from "../../../firebase.ts";
import {
    collection,
    getDocs,
    deleteDoc,
    query,
    orderBy,
    updateDoc
} from "firebase/firestore";
import { User } from "firebase/auth";
import { HbMain } from "../../hb-main/hb-main.ts";
import { redirect, wait } from "../../global.ts";

import { Modal } from "flowbite";
import type { ModalOptions, ModalInterface } from "flowbite";
import type { InstanceOptions } from "flowbite";

export class HbSafe extends HTMLElement {
    private heartbank: HeartBank = new HeartBank();
    private crypto: Crypto = new Crypto();
    private modal: ModalInterface;
    private main: HbMain;

    private safe: HTMLElement;
    private heartkeys: HTMLUListElement;
    private add: HTMLButtonElement;
    private message: HTMLParagraphElement;

    private heartkeysCount = 0;

    constructor() {
        super();
        render(template(null), this);
        this.main = document.querySelector("hb-main") as HbMain;

        this.safe = this.querySelector("#hb-safe")!;
        this.heartkeys = this.querySelector("#hb-safe-heartkeys")!;
        this.add = this.querySelector("#hb-safe-add")!;
        this.message = this.querySelector("#hb-safe-message")!;

        const modalOptions: ModalOptions = {
            placement: "center",
            backdrop: "dynamic",
            backdropClasses:
                "bg-gray-900/50 dark:bg-gray-900/80 fixed inset-0 z-40",
            closable: true,
            onHide: () => {
                //window.location.hash = "#";
            },
            onShow: () => {
                //console.log("modal is shown");
            },
            onToggle: () => {
                //console.log("modal has been toggled");
            }
        };

        // instance options object
        const instanceOptions: InstanceOptions = {
            id: "hb-safe",
            override: true
        };

        this.modal = new Modal(this.safe, modalOptions, instanceOptions);
    }

    connectedCallback() {
        this.add.disabled = !this.heartbank.isPasskeySupported();
        this.add.addEventListener("click", this.addHeartkey.bind(this));

        this.heartbank.addAuthStateListener((user) => {
            if (user) {
                this.render(user);
            }
        });
    }

    private async addHeartkey() {
        this.add.disabled = true;
        if (this.heartbank.user?.phoneNumber) {
            this.message.style.color = "orange";
            this.message.textContent = "Registering Safe...";
            const name =
                localStorage.getItem("safe") ||
                prompt("Please enter a name for this Safe:");
            if (name) {
                const status = await this.crypto.add(name);
                if (status.color === "green") {
                    if (this.heartkeysCount === 0) {
                        await wait(1); // if not good then refactor to onSnapshot
                        this.message.textContent =
                            "Two Safes have been created, the first of which is your Primary Safe (publicly tied to your profile for others to discover)";
                    } else
                        this.message.textContent =
                            "A new Safe has been created successfully (not publicly tied to your profile)"; // status.text
                    this.render(this.heartbank.user!);
                    redirect("safes");
                } else
                    this.message.textContent =
                        "Sorry, an error occurred. Please try again.";
                if (status.text.includes("focus"))
                    localStorage.setItem("safe", name);
                else localStorage.removeItem("safe");
                this.message.style.color = status.color;
            } else {
                this.message.textContent = "";
            }
        } else {
            if (
                confirm(
                    "Sorry, only humans with a Heart of Mithril or above can create Safes. Would you like to prove your humanity by confirming your phone number?"
                )
            )
                window.location.hash = "#/accouont";
        }
        this.add.disabled = false;
    }

    async render(user: User) {
        this.main.renderSilver(user); // await

        if (
            this.heartbank.isPasskeySupported() &&
            this.heartbank.user?.phoneNumber
        ) {
            const keysQuery = query(
                collection(fs, "users", user.uid, "heartkeys"),
                orderBy("name")
            );
            const keysSnap = await getDocs(keysQuery);
            this.heartkeysCount = keysSnap.size;

            if (keysSnap.empty) {
                this.heartkeys.innerHTML =
                    "<p class='p-3'>You have not registered any Safes</p>";
                this.heartkeys.style.color = "gray";
            } else {
                this.heartkeys.innerHTML = "";
                this.heartkeys.style.color = "";

                keysSnap.docs.forEach((doc, index) => {
                    const { name, isPrimary } = doc.data();
                    const li = document.createElement("li");
                    li.classList.add(
                        index === this.heartkeysCount - 1 ? "mb-0" : "mb-4",
                        "bg-white",
                        "dark:bg-gray-800",
                        "border",
                        "border-gray-200",
                        "dark:border-gray-700",
                        "shadow-sm",
                        "rounded-lg",
                        "p-4"
                    );

                    const button1 = document.createElement("button");
                    button1.innerHTML = "🔒&nbsp;&nbsp;" + name;
                    button1.classList.add("ml-1");
                    button1.onclick = async () => {
                        const newName = prompt(
                            "New name for your Safe:",
                            name
                        )?.trim();
                        if (newName && newName !== name) {
                            await updateDoc(doc.ref, { name: newName });
                            this.message.textContent = `${name} has been renamed to ${newName}`;
                            this.message.style.color = "green";
                            this.render(user);
                        }
                    };

                    const button2 = document.createElement("button");
                    button2.textContent = "🌐";
                    button2.classList.add("mr-0");
                    if (!isPrimary) button2.classList.add("opacity-30");
                    button2.onclick = async () => {
                        if (isPrimary) {
                            this.message.textContent =
                                name +
                                " is your Primary Safe which means it is publicly tied to your profile for others to discover. You can rotate it out at anytime to dissociate it to your profle.";
                            this.message.style.color = "orange";
                        } else {
                            if (
                                confirm(
                                    `Would you like to make ${name} your Primary Safe instead? This means it will be publicly tied to your profile for others to discover.`
                                )
                            ) {
                                //TODO: do try catch for error?
                                const status = await this.crypto.set(
                                    doc.ref.id
                                );
                                this.message.textContent =
                                    name +
                                    " is now your Primary Safe. Going forward, your previous Primary Safe will no longer be associated to your profile.";
                                this.message.style.color = "green";
                                this.render(user);
                            }
                        }
                    };

                    const h2 = document.createElement("h2");
                    h2.classList.add(
                        "w-full",
                        "inline-flex",
                        "justify-between",
                        "text-lg",
                        "font-semibold",
                        "text-gray-900",
                        "dark:text-white",
                        "mb-2"
                    );
                    h2.append(button1, button2);

                    const label = document.createElement("label");
                    label.classList.add(
                        "block",
                        "my-2",
                        "text-sm",
                        "font-medium",
                        "text-gray-900",
                        "dark:text-white"
                    );
                    label.textContent = "Safe Address ℹ️";

                    const input = document.createElement("input");
                    input.type = "text";
                    input.classList.add(
                        "row-span-6",
                        "bg-gray-50",
                        "border",
                        "border-gray-300",
                        "text-gray-500",
                        "text-sm",
                        "rounded-lg",
                        "focus:ring-blue-500",
                        "focus:border-blue-500",
                        "block",
                        "w-full",
                        "p-2.5",
                        "dark:bg-gray-800",
                        "dark:border-gray-600",
                        "dark:placeholder-gray-400",
                        "dark:text-gray-400",
                        "dark:focus:ring-blue-500",
                        "dark:focus:border-blue-500"
                    );
                    input.disabled = true;
                    input.readOnly = true;
                    input.value = doc.id.truncate(); //11, 11 weird ts error

                    const button3 = document.createElement("button");
                    button3.classList.add(
                        "cursor-pointer",
                        "w-3.5",
                        "h-3.5",
                        "absolute",
                        "end-2",
                        "top-1/2",
                        "-translate-y-1/2",
                        "text-gray-500",
                        "dark:text-gray-400",
                        "hover:bg-gray-100",
                        "dark:hover:bg-gray-800",
                        "rounded-lg",
                        "p-2",
                        "inline-flex",
                        "items-center",
                        "justify-center"
                    );
                    button3.onclick = async () => {
                        await navigator.clipboard.writeText(doc.id);
                        this.message.textContent =
                            name +
                            "'s Safe Address has been copied to your clipboard!";
                        this.message.style.color = "green";
                    };
                    button3.textContent = "📋";

                    const div = document.createElement("div");
                    div.classList.add("relative", "mb-2");
                    div.append(input, button3);

                    const address = document.createElement("address");
                    address.classList.add(
                        "bg-gray-50",
                        "dark:bg-gray-700",
                        "dark:border-gray-600",
                        "p-4",
                        "rounded-lg",
                        "border",
                        "border-gray-200",
                        "not-italic",
                        "flex-row"
                    );
                    address.append(label, div);

                    li.append(h2, address);
                    this.heartkeys.appendChild(li);
                });
            }
        } else {
            this.heartkeys.innerHTML = this.heartbank.isPasskeySupported()
                ? "<p class='p-3'>You have not registered any Safes</p>" // "<p class='p-3'>Only humans with a Heart of Mithril or above can create Safes. Please prove your humanity by confirming your phone number.</p>"
                : "<p class='p-3'>Safes are not supported on this device.</p>";
            this.heartkeys.style.color = "gray";
        }
    }

    // deprecated
    // show all past keys // cannot remove heartkey
    // primary key = public
    // secondary keys = anonymous // can add/rotate new key for privacy!
    async render2(user: User) {
        await this.main.renderSilver(user);

        if (this.heartbank.isPasskeySupported()) {
            // const keysQuery = query(
            //     collection(fs, "users", uid, "heartkeys"),
            //     orderBy("createdAt", "desc")
            // );
            const keysQuery = query(
                collection(fs, "users", user.uid, "heartkeys"),
                orderBy("name")
            );
            const keysSnap = await getDocs(keysQuery);
            this.heartkeysCount = keysSnap.size;

            if (keysSnap.empty) {
                this.heartkeys.innerHTML = "You have not registered any Safes";
                this.heartkeys.style.color = "gray";
            } else {
                this.heartkeys.innerHTML = "";
                this.heartkeys.style.color = "";

                keysSnap.docs.forEach((doc, index) => {
                    const { name, isPrimary } = doc.data();
                    const li = document.createElement("li");
                    li.classList.add("flex", "items-center", "justify-between");

                    const span = document.createElement("span");
                    span.textContent = "🔐 " + name;
                    span.classList.add("cursor-pointer");
                    if (!isPrimary) span.classList.add("opacity-50"); //"line-through",
                    span.onclick = async () => {
                        const newName = prompt(
                            "New name for your Safe:",
                            name
                        )?.trim();
                        if (newName && newName !== name) {
                            await updateDoc(doc.ref, { name: newName });
                            this.render2(user);
                        }
                    };

                    // deprecated!
                    // const privateBtn = document.createElement("button");
                    // privateBtn.classList.add("cursor-pointer");
                    // privateBtn.textContent = "🔐 Private Key";
                    // if (index !== 0) privateBtn.classList.add("opacity-50");
                    // privateBtn.onclick = async () => {
                    //     const {
                    //         text,
                    //         color,
                    //         iv,
                    //         encryptedSymmetricKey,
                    //         encryptedContent: encryptedPrivateKey,
                    //         signature
                    //     } = await this.crypto.get(doc.ref.id);
                    //     this.message.textContent = text;
                    //     this.message.style.color = color;
                    //     // generate secret gem
                    // };

                    const publicBtn = document.createElement("button");
                    publicBtn.classList.add("cursor-pointer");
                    publicBtn.textContent = "📋 Safe Address";
                    if (!isPrimary) publicBtn.classList.add("opacity-50");
                    publicBtn.onclick = async () => {
                        await navigator.clipboard.writeText(doc.id);
                        this.message.textContent =
                            "The Safe Address has been copied to your clipboard!";
                        this.message.style.color = "green";
                    };

                    const primaryBtn = document.createElement("button");
                    primaryBtn.classList.add("cursor-pointer");
                    primaryBtn.textContent = "🌐";
                    if (!isPrimary) primaryBtn.classList.add("opacity-30");
                    primaryBtn.onclick = async () => {
                        if (isPrimary) {
                            this.message.textContent =
                                name +
                                " is your Primary Safe which means it is publicly tied to your profile for others to discover. You can rotate it out at anytime to dissociate it to your profle.";
                            this.message.style.color = "orange";
                        } else {
                            if (
                                confirm(
                                    `Would you like to make ${name} your Primary Safe instead? This means it will be publicly tied to your profile for others to discover.`
                                )
                            ) {
                                const status = await this.crypto.set(
                                    doc.ref.id
                                );
                                this.message.textContent =
                                    name +
                                    " is now your Primary Safe. Going forward, your old primary Safe will no longer be associated to your profile.";
                                this.message.style.color = "green";
                                this.render(user);
                            }
                        }
                    };

                    li.append(span, " ", publicBtn, primaryBtn);
                    this.heartkeys.appendChild(li);
                });
            }
        } else {
            this.heartkeys.innerHTML =
                "Safes are not supported on this device.";
            this.heartkeys.style.color = "gray";
        }
    }

    show() {
        this.modal.show();
    }

    hide() {
        this.modal.hide();
    }
}
